// src/components/DayTableFit.tsx
import React, { useMemo, useCallback, useState, useRef } from 'react';
import AvailabilityOverlay from './AvailabilityOverlay'; // Import the overlay
import { formatTimeFromMinutes } from '../utils/helpers';
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES, timeSlots, daysOfWeek } from '../config/constants';
import { getColorForCourse } from '../utils/colorUtils';
import ScheduleEntry from '../models/ScheduleEntry'; // Import the ScheduleEntry class
import { availabilityService, Severity } from '../services/AvailabilityService';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import HolidayOverlay from './HolidayOverlay';
import ScheduleEntryDetailView from './ScheduleEntryDetailView';
import type { Holiday } from '../services/AcademicYearService';
import type { DraggedScheduleEntry } from '../types/dragAndDrop';

// --- Configuration ---
const GRID_DAY_HEADER_HEIGHT_PX = 25;
const GRID_ROOM_HEADER_HEIGHT_PX = 25;
const GRID_TIME_COL_WIDTH_PX = 60; // Adjust if needed

// Define types for the component
interface AvailabilityStatus {
    teacherStatus: Severity;
    groupStatus: Severity;
    isRoomOccupied: boolean;
    isRoomValidForSubject: boolean;
    overallStatus: Severity;
    conflictReasons: Array<{ reason: string; severity: Severity }>;
    conflictReason?: string;
}

// DraggedItemInfo interface removed - now using DraggedScheduleEntry from types/dragAndDrop

interface FitDragOverData {
    roomIndex: number;
    targetTimeMinutes: number;
    availabilityStatus: AvailabilityStatus;
}

interface OverlayInfo {
    isVisible: boolean;
    reasons: Array<{ reason: string; severity: Severity }>;
    position: { top: number; left: number };
}

interface TimeLabel {
    time: string;
    style: { top: string; transform: string };
}

interface ItemStyle {
    display?: string;
    position?: 'absolute';
    top?: string;
    height?: string;
    gridColumnStart?: number;
    gridColumnEnd?: number;
    width?: string;
    zIndex?: number;
    overflow?: string;
    minHeight?: string;
    left?: string;
}

interface DayTableFitProps {
    day: string;
    rooms: string[];
    items: ScheduleEntry[];
    dayScheduleEntry: ScheduleEntry[];
    user: { role: string };
    onOpenModal: (item: ScheduleEntry) => void;
    availableContentHeight?: number;
    onItemDrop: (itemId: string, room: string, day: string, startTime: string) => void;
    draggedItemInfo: DraggedScheduleEntry;
    onGlobalDragStart: (scheduleEntry: ScheduleEntry, clickOffsetY: number) => void;
    onGlobalDragEnd: () => void;
    availabilityOverlayData?: Map<string, AvailabilityStatus>;
    KEY_SEPARATOR: string;
    teachers: Teacher[];
    groups: Group[];
    subjects: Subject[];
    currentWeekNumber?: number;
    currentYear?: number;
    dragToCreateData?: {
        isActive: boolean;
        startTime: number | null;
        endTime: number | null;
        room: string | null;
        day: string | null;
        startPosition: { x: number; y: number };
        currentPosition: { x: number; y: number };
    };
    onDragToCreateStart?: (day: string, room: string, startTimeMinutes: number, startPosition: { x: number; y: number }) => void;
    onDragToCreateMove?: (currentPosition: { x: number; y: number }, currentTimeMinutes: number) => void;
    onDragToCreateEnd?: () => void;
    onDragToCreateCancel?: () => void;
    isPublicView?: boolean;
    holiday?: Holiday | null;
    readOnly?: boolean;
}

// Define colors for the overlay (using semi-transparent backgrounds)
const AVAILABILITY_COLORS = {
    red: 'bg-red-400/10', // Semi-transparent red
    orange: 'bg-orange-300/50', // Semi-transparent orange
    green: 'bg-transparent' // No overlay for available slots
};

const CHECK_INTERVAL_MINUTES = 15;

/**
 * Renders a schedule grid for a single day, fitting content within available height.
 * Features: Item positioning, click modal, drag & drop with availability highlighting.
 */
const DayTableFit: React.FC<DayTableFitProps> = ({
    day,
    rooms = [],
    items = [],
    dayScheduleEntry, // <-- RENAMED: Day-specific schedule entries for conflict checks
    user,
    onOpenModal,
    availableContentHeight = 500, // Default value
    // --- NEW Props for Drag & Drop ---
    onItemDrop,
    draggedItemInfo,
    onGlobalDragStart,
    onGlobalDragEnd,
    availabilityOverlayData, // Optional pre-calculated data
    KEY_SEPARATOR, // Separator used in overlay keys
    teachers,
    groups,
    subjects,
    currentWeekNumber,
    currentYear,
    dragToCreateData,
    onDragToCreateStart,
    onDragToCreateMove,
    onDragToCreateEnd,
    onDragToCreateCancel,
    isPublicView = false,
    holiday,
    readOnly = false
}) => {
    const gridRef = useRef<HTMLDivElement | null>(null); // Ref for the main grid container
    const isAdmin = user?.role === 'Admin';
    const allowEditing = isAdmin && !readOnly;

    // --- Local State for Drag Over in Fit View ---
    const [fitDragOverData, setFitDragOverData] = useState<FitDragOverData | null>(null); // { roomIndex, targetTimeMinutes, availabilityStatus }
    const [fitOverlayInfo, setFitOverlayInfo] = useState<OverlayInfo>({
        isVisible: false,
        reasons: [] as Array<{ reason: string; severity: Severity }>,
        position: { top: 0, left: 0 }
    });

    // Read-only detail modal state for public users
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [detailEntry, setDetailEntry] = useState<ScheduleEntry | null>(null);

    // --- Memoized Lookups and Calculations ---
    const roomIndexMap = useMemo(() => new Map(rooms.map((room, index) => [room, index])), [rooms]);

    const totalGridContentHeight = useMemo(() => {
        return Math.max(1, availableContentHeight); // Use prop, ensure > 0
    }, [availableContentHeight]);

    // Convert items to ScheduleEntry objects
    const scheduleEntries = useMemo(() => {
        // items should already be ScheduleEntry objects, but ensure they are
        return items.map(item => item instanceof ScheduleEntry ? item : ScheduleEntry.fromObject(item));
    }, [items]);

    // dayScheduleEntry should already be ScheduleEntry objects
    const baseScheduleEntries = useMemo(() => {
        return dayScheduleEntry || [];
    }, [dayScheduleEntry]);

    // --- Helper function to detect overlapping items ---
    const getOverlappingGroups = useCallback((entries: ScheduleEntry[]) => {
        const groups: ScheduleEntry[][] = [];

        for (const entry of entries) {
            if (!entry || !entry.room || !entry.startMinutes || !entry.durationMinutes) continue;

            // Find if this entry overlaps with any existing group
            let addedToGroup = false;

            for (const group of groups) {
                // Check if this entry overlaps with any item in this group
                const overlapsWithGroup = group.some(groupEntry => {
                    return (
                        groupEntry.room === entry.room &&
                        groupEntry.day === entry.day &&
                        groupEntry.startMinutes !== null &&
                        groupEntry.durationMinutes !== null &&
                        entry.startMinutes !== null &&
                        entry.durationMinutes !== null &&
                        // Check time overlap: two items overlap if one starts before the other ends
                        groupEntry.startMinutes < (entry.startMinutes + entry.durationMinutes) &&
                        entry.startMinutes < (groupEntry.startMinutes + groupEntry.durationMinutes)
                    );
                });

                if (overlapsWithGroup) {
                    group.push(entry);
                    addedToGroup = true;
                    break;
                }
            }

            // If not added to any existing group, create a new group
            if (!addedToGroup) {
                groups.push([entry]);
            }
        }

        return groups;
    }, []);

    // --- Memoized overlapping groups calculation ---
    const overlappingGroups = useMemo(() => {
        return getOverlappingGroups(scheduleEntries);
    }, [scheduleEntries, getOverlappingGroups]);

    // --- Enhanced Item Styling (Uses totalGridContentHeight derived from prop) ---
    const getItemStyle = useCallback((item: ScheduleEntry): ItemStyle => {
        if (!item || !item.shouldBeInCalendar()) return { display: 'none' };
        const roomIndex = roomIndexMap.get(item.room);
        if (roomIndex === undefined) return { display: 'none' };

        const { startMinutes, durationMinutes } = item.parseTime();
        if (startMinutes === null || durationMinutes === null || durationMinutes <= 0 || TOTAL_SCHEDULE_MINUTES <= 0 || totalGridContentHeight <= 0) {
            return { display: 'none' };
        }

        // Find the overlapping group this entry belongs to
        const overlappingGroup = overlappingGroups.find(group => group.includes(item));
        const isOverlapping = overlappingGroup && overlappingGroup.length > 1;

        const offsetMinutes = Math.max(0, startMinutes - SCHEDULE_START_MINUTE);
        const maxPossibleDuration = TOTAL_SCHEDULE_MINUTES - offsetMinutes;
        const clampedDurationMinutes = Math.min(durationMinutes, maxPossibleDuration);

        const topPercent = (offsetMinutes / TOTAL_SCHEDULE_MINUTES) * 100;
        const heightPercent = (clampedDurationMinutes / TOTAL_SCHEDULE_MINUTES) * 100;
        const gridColumnStart = roomIndex + 2; // +1 for time col, +1 for 1-based index

        let width = '100%';
        let left = '0%';

        if (isOverlapping) {
            // Calculate position within the overlapping group
            const groupSize = overlappingGroup.length;
            const indexInGroup = overlappingGroup.indexOf(item);

            // Each item takes up 1/groupSize of the width
            const itemWidthPercent = 100 / groupSize;
            width = `${itemWidthPercent}%`;
            left = `${indexInGroup * itemWidthPercent}%`;
        }

        return {
            position: 'absolute',
            top: `${topPercent}%`,
            height: `${heightPercent}%`,
            left: left,
            gridColumnStart: gridColumnStart,
            gridColumnEnd: gridColumnStart + 1,
            width: width, // Modified to handle overlaps
            zIndex: 10,
            overflow: 'hidden',
            minHeight: '10px', // Ensure visibility for short items
        };
    }, [roomIndexMap, totalGridContentHeight, overlappingGroups]);

    // --- Time Labels ---
    const timeLabels = useMemo<TimeLabel[]>(() => {
        const labels: TimeLabel[] = [];
        const hourIntervalMinutes = 60;
        if (totalGridContentHeight <= 0) return [];
        for (let minutes = SCHEDULE_START_MINUTE; minutes < SCHEDULE_END_MINUTE; minutes += hourIntervalMinutes) {
            const topPercent = ((minutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * 100;
            labels.push({
                time: formatTimeFromMinutes(minutes),
                style: { top: `${topPercent}%`, transform: 'translateY(-50%)' }
            });
        }
        return labels;
    }, [totalGridContentHeight]);

    // --- Drag and Drop Handlers for Fit View ---
    const handleItemDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, item: ScheduleEntry) => {
        if (!allowEditing || !item) { e.preventDefault(); return; }
        const clickOffsetY = (e.nativeEvent as any).offsetY || 0;

        onGlobalDragStart(item, clickOffsetY); // Pass ScheduleEntry directly to parent handler
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'move';
        if (e.currentTarget?.style) {
            e.currentTarget.style.opacity = '0.5';
            e.currentTarget.style.cursor = 'grabbing';
        }
    }, [allowEditing, onGlobalDragStart]);

    const handleFitDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!allowEditing || !draggedItemInfo.entry || !baseScheduleEntries) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!gridRef.current) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const cursorX = e.clientX - gridRect.left;
        const cursorY = e.clientY - gridRect.top;

        // --- Calculate Target Room Index (Horizontal) ---
        const roomAreaWidth = gridRect.width - GRID_TIME_COL_WIDTH_PX;
        const roomColumnWidth = roomAreaWidth > 0 ? roomAreaWidth / rooms.length : 1;
        let targetRoomIndex = Math.floor((cursorX - GRID_TIME_COL_WIDTH_PX) / roomColumnWidth);
        targetRoomIndex = Math.max(0, Math.min(targetRoomIndex, rooms.length - 1));
        const targetRoomName = rooms[targetRoomIndex];

        // Add null check for targetRoomName
        if (!targetRoomName) return;

        // --- Calculate Target Time (Vertical) ---
        const contentCursorY = Math.max(0, cursorY - GRID_ROOM_HEADER_HEIGHT_PX); // Adjust for room header row
        const contentClickOffsetY = draggedItemInfo.clickOffsetY || 0;
        // Calculate vertical position relative to the *content* area height
        const contentHeight = gridRect.height - GRID_ROOM_HEADER_HEIGHT_PX;
        if (contentHeight <= 0) return; // Avoid division by zero

        const targetTopPx = Math.max(0, contentCursorY - contentClickOffsetY);
        const minutesOffset = (targetTopPx / contentHeight) * TOTAL_SCHEDULE_MINUTES;
        let targetTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;

        // Clamp target time within schedule bounds, considering item duration
        const minStartTime = SCHEDULE_START_MINUTE;
        const maxStartTime = SCHEDULE_END_MINUTE - (draggedItemInfo.durationMinutes || 0);
        targetTimeMinutes = Math.max(minStartTime, Math.min(targetTimeMinutes, maxStartTime));
        const roundingStep = 5; // Snap to 5-minute intervals
        targetTimeMinutes = Math.round(targetTimeMinutes / roundingStep) * roundingStep;

        // --- Check Availability ---
        const draggedItemObject = draggedItemInfo.entry;
        let availabilityStatus: AvailabilityStatus = {
            teacherStatus: 'red' as Severity,
            groupStatus: 'red' as Severity,
            isRoomOccupied: false,
            isRoomValidForSubject: false,
            overallStatus: 'red' as Severity,
            conflictReason: 'Dragged item not found.',
            conflictReasons: [{ reason: 'Dragged item not found.', severity: 'red' as Severity }]
        };
        if (draggedItemObject && targetRoomName) {
            const result = availabilityService.checkAvailability(
                draggedItemObject, targetRoomName, day, targetTimeMinutes,
                baseScheduleEntries, teachers, groups, subjects
            );
            availabilityStatus = {
                ...result,
                teacherStatus: result.teacherStatus as Severity,
                groupStatus: result.groupStatus as Severity,
                overallStatus: result.overallStatus as Severity,
                conflictReasons: result.conflictReasons.map(reason => ({
                    ...reason,
                    severity: reason.severity as Severity
                }))
            };
        } else {
            console.warn(`[${day}-Fit] handleDragOver: No dragged entry available.`);
            availabilityStatus.conflictReasons.push({ reason: 'Dragged item not found.', severity: 'red' as Severity });
        }

        // Update local state for visual feedback
        setFitDragOverData({
            roomIndex: targetRoomIndex,
            targetTimeMinutes,
            availabilityStatus
        });

        // --- Update Overlay Info ---
        const isUnavailable = availabilityStatus.overallStatus === 'red' || availabilityStatus.overallStatus === 'orange';
        if (isUnavailable && availabilityStatus.conflictReasons && availabilityStatus.conflictReasons.length > 0) {
            // Calculate position relative to the grid container for better positioning
            const markerLeftPx = GRID_TIME_COL_WIDTH_PX + (targetRoomIndex * roomColumnWidth);
            // Check if there's enough space on the right, otherwise position on the left
            const rightSpace = window.innerWidth - (gridRect.left + markerLeftPx + roomColumnWidth + 350); // 350px for overlay width
            const overlayLeft = rightSpace > 0
                ? markerLeftPx + roomColumnWidth + 15 // Position on the right
                : markerLeftPx - 350 - 15; // Position on the left if not enough space

            // Calculate the center of the target time slot for better vertical alignment
            const timeSlotHeight = (totalGridContentHeight * (draggedItemInfo.durationMinutes || 60)) / TOTAL_SCHEDULE_MINUTES;
            const centerY = targetTopPx + (timeSlotHeight / 2);
            const overlayTop = centerY - 20; // Center vertically on the time slot, offset by 20px

            setFitOverlayInfo({
                isVisible: true,
                reasons: availabilityStatus.conflictReasons.map(reason => ({
                    reason: reason.reason,
                    severity: reason.severity as Severity
                })),
                // Position relative to the grid container
                position: { top: overlayTop, left: overlayLeft }
            });
        } else {
            setFitOverlayInfo(prev => ({ ...prev, isVisible: false }));
        }

    }, [allowEditing, draggedItemInfo, rooms, day, baseScheduleEntries, teachers, groups, subjects, currentWeekNumber, currentYear]); // Removed totalGridContentHeight and availableContentHeight

    const handleItemDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!allowEditing) return;
        // Reset opacity and cursor on the dragged element itself
        if (e.currentTarget?.style) {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.cursor = 'grab';
        }
        // Call the global handler from the parent to clear global state
        onGlobalDragEnd();
        // Clear local drag-over state for this specific grid
        setFitDragOverData(null);
        setFitOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } });
    }, [allowEditing, onGlobalDragEnd]);
    // --- END FIX ---

    const handleFitDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!allowEditing) return;
        // Check if the related target (where the mouse moved to) is outside the grid
        if (gridRef.current && !gridRef.current.contains(e.relatedTarget as Node)) {
            setFitDragOverData(null);
            setFitOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } });
        }
    }, [allowEditing]);

    const handleFitDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!allowEditing) return;
        e.preventDefault();
        const itemId = e.dataTransfer.getData('text/plain');

        setFitOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } }); // Hide Overlay

        if (!itemId || !fitDragOverData || !baseScheduleEntries) {
            console.error(`[${day}-Fit] Drop failed: Missing data.`, { itemId, fitDragOverData });
            setFitDragOverData(null);
            onGlobalDragEnd(); // Ensure parent state is cleared
            return;
        }

        const targetRoom = rooms[fitDragOverData.roomIndex];
        const finalTargetStartMinute = fitDragOverData.targetTimeMinutes;
        const newStartTimeString = formatTimeFromMinutes(finalTargetStartMinute);

        // Call the parent's drop handler - IT WILL HANDLE CONFIRMATION
        if (onItemDrop && targetRoom && newStartTimeString) {
            onItemDrop(itemId, targetRoom, day, newStartTimeString);
        } else {
            console.error(`[${day}-Fit] Failed to trigger parent drop handler.`);
            onGlobalDragEnd(); // Ensure parent state is cleared if drop handler fails
        }

        setFitDragOverData(null); // Clear local state after attempting drop

    }, [allowEditing, fitDragOverData, rooms, day, onItemDrop, onGlobalDragEnd, baseScheduleEntries]);

    // --- Render Component ---
    return (
        <div className="h-full flex flex-col"> {/* Use flex-col and h-full */}
            {/* Header remains fixed height */}
            <h2 className="text-sm font-semibold p-1 capitalize border-b bg-gray-100 text-gray-700 text-center flex items-center justify-center flex-shrink-0" style={{ height: `${GRID_DAY_HEADER_HEIGHT_PX}px` }}>
                {day}
            </h2>
            {/* Container for the grid takes remaining height */}
            <div
                ref={gridRef} // Add ref here
                className="relative flex-grow bg-white overflow-hidden"
                // Add DND listeners to the grid container
                onDragOver={allowEditing ? handleFitDragOver : undefined}
                onDragLeave={allowEditing ? handleFitDragLeave : undefined}
                onDrop={allowEditing ? handleFitDrop : undefined}
            >
                {/* Grid Structure */}
                <div
                    className="absolute inset-0"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `${GRID_TIME_COL_WIDTH_PX}px repeat(${rooms.length}, 1fr)`,
                        gridTemplateRows: `${GRID_ROOM_HEADER_HEIGHT_PX}px 1fr`, // Room header row + content row
                        height: '100%',
                    }}
                >
                    {/* Empty Cell: Top-left corner */}
                    <div className="sticky top-0 left-0 z-30 bg-gray-50 border-b border-r border-gray-200" style={{ gridColumn: 1, gridRow: 1 }}></div>

                    {/* Room Name Headers */}
                    {rooms.map((room, index) => (
                        <div
                            key={`room-header-${room}-${index}`}
                            className="p-1 border-b border-r border-gray-200 text-center text-[10px] font-medium text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis sticky top-0 z-20 bg-gray-50 flex items-center justify-center"
                            style={{ gridColumn: index + 2, gridRow: 1 }}
                            title={room}
                        >
                            <span>{room}</span>
                        </div>
                    ))}

                    {/* Time label area */}
                    <div className="relative border-r border-gray-200 bg-gray-50 z-20" style={{ gridColumn: 1, gridRow: 2 }}>
                        {timeLabels.map(label => (
                            <div key={`time-${label.time}`} className="absolute right-0 text-right text-[10px] font-medium text-gray-500 pr-1 pointer-events-none" style={label.style}>
                                <span>{label.time}</span>
                            </div>
                        ))}
                    </div>

                    {/* Room content columns */}
                    {rooms.map((room, index) => (
                        <div key={`room-col-${room}-${index}`} className="relative border-r border-gray-200 last:border-r-0" style={{ gridColumn: index + 2, gridRow: 2 }}>
                            {/* Vertical Grid Line */}
                            {index < rooms.length - 1 && (
                                <div className="absolute top-0 bottom-0 right-0 border-l border-dotted border-gray-100 pointer-events-none z-1"></div>
                            )}
                            {/* Horizontal Grid Lines */}
                            {timeLabels.map(label => (
                                <div key={`hline-${label.time}-${index}`} className="absolute left-0 right-0 border-t border-dotted border-gray-200 pointer-events-none" style={{ top: label.style.top, zIndex: 1 }}></div>
                            ))}
                        </div>
                    ))}

                    {/* Holiday Overlay */}
                    {holiday && (
                        <div
                            className="absolute"
                            style={{
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                gridColumn: `2 / span ${rooms.length}`, // Cover all room columns
                                gridRow: 2, // Cover the content row
                                zIndex: 4 // Behind schedule items but above grid lines
                            }}
                        >
                            <HolidayOverlay holiday={holiday} />
                        </div>
                    )}

                    {allowEditing && draggedItemInfo.entry && availabilityOverlayData && rooms.length > 0 && (
                        Array.from(availabilityOverlayData.entries()).map(([key, status]: [string, AvailabilityStatus]) => {
                            const keyParts = key.split(KEY_SEPARATOR);
                            if (keyParts.length !== 3 || keyParts[0] !== day) return null;

                            const roomName = keyParts[1];
                            const timeMinutesStr = keyParts[2];

                            // Add null checks for array elements
                            if (!roomName || !timeMinutesStr) return null;

                            const timeMinutes = parseInt(timeMinutesStr, 10);
                            const roomIndex = roomIndexMap.get(roomName);

                            if (roomIndex === undefined || isNaN(timeMinutes) || status.overallStatus === 'green') {
                                return null;
                            }
                            // Don't show for simple room occupancy if a specific item is already there.
                            // The drag-over marker will indicate that. This is for preference conflicts mostly.
                            const isOnlyRoomOccupancy = status.overallStatus === 'red' &&
                                status.conflictReasons &&
                                status.conflictReasons.length === 1 &&
                                status.conflictReasons[0]?.reason?.startsWith('Room (') &&
                                status.conflictReasons[0]?.reason?.includes(' occupied by ');
                            if (isOnlyRoomOccupancy) return null;

                            const offsetMinutes = timeMinutes - SCHEDULE_START_MINUTE;
                            const topPercent = (offsetMinutes / TOTAL_SCHEDULE_MINUTES) * 100;
                            const heightPercent = (CHECK_INTERVAL_MINUTES / TOTAL_SCHEDULE_MINUTES) * 100;

                            if (topPercent < 0 || (topPercent + heightPercent) > 100) return null; // Ensure it's within bounds

                            return (
                                <div
                                    key={key}
                                    className={`absolute pointer-events-none ${AVAILABILITY_COLORS[status.overallStatus] || AVAILABILITY_COLORS.green}`}
                                    title={`${roomName} @ ${formatTimeFromMinutes(timeMinutes)}: ${status.conflictReasons?.map(r => r.reason).join(', ') || status.overallStatus}`}
                                    style={{
                                        top: `${topPercent}%`,
                                        height: `${heightPercent}%`,
                                        gridColumnStart: roomIndex + 2, // +1 for time col, +1 for 1-based index
                                        gridColumnEnd: roomIndex + 3,
                                        width: '100%',
                                        zIndex: 5, // Should be behind schedule items and drag marker
                                        gridRow: 2, // Ensure it's in the content row area
                                    }}
                                />
                            );
                        })
                    )}

                    {/* Schedule Items */}
                    {scheduleEntries.map(entry => {
                        const style = getItemStyle(entry);
                        if (style.display === 'none') return null;
                        const itemBgColor = getColorForCourse(entry.course || entry.type);
                        return (
                            <div
                                key={entry.id}
                                className={`rounded border text-[9px] leading-tight shadow-sm ${itemBgColor} ${allowEditing ? 'cursor-grab active:cursor-grabbing' : isPublicView ? 'cursor-default' : 'cursor-pointer'}`}
                                style={{ ...style, gridRow: 2 }} // Ensure item is placed in the content row
                                title={`${entry.course || entry.type} - ${entry.teacher || ''}\n${entry.timeRangeDisplay()} @ ${entry.room}`}
                                onClick={!allowEditing ? () => { setDetailEntry(entry); setIsDetailOpen(true); } : () => onOpenModal(entry)}
                                // --- Add DND Handlers to Items ---
                                draggable={allowEditing}
                                onDragStart={allowEditing ? (e) => handleItemDragStart(e, entry) : undefined}
                                onDragEnd={allowEditing ? handleItemDragEnd : undefined}
                            >
                                <p className="font-semibold truncate px-0.5">{entry.course || entry.type?.toUpperCase() || 'N/A'}</p>
                                {entry.teacher && <p className="truncate px-0.5 opacity-80">{entry.teacher}</p>}
                                <p className="truncate px-0.5 text-[8px] opacity-70">{entry.timeRangeDisplay().split(' - ')[0]}</p>
                            </div>
                        );
                    })}

                    {/* --- Drag Over Marker for Fit View --- */}
                    {allowEditing && fitDragOverData && draggedItemInfo.entry && (
                        <>
                            {/* Time Display (Positioned Above Marker) */}
                            <div
                                className="absolute pointer-events-none"
                                style={{
                                    gridColumnStart: fitDragOverData.roomIndex + 2,
                                    gridColumnEnd: fitDragOverData.roomIndex + 3,
                                    // Position relative to content area (row 2)
                                    top: `${((fitDragOverData.targetTimeMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * 100}%`,
                                    transform: 'translateY(-100%) translateY(-4px)', // Position above the marker
                                    zIndex: 51,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gridRow: 2, // Ensure it's in the content row
                                }}
                            >
                                <span className={`text-[10px] font-semibold text-white px-1 py-0.5 rounded shadow-md ${fitDragOverData.availabilityStatus?.overallStatus === 'red' ? 'bg-red-600' :
                                    fitDragOverData.availabilityStatus?.overallStatus === 'orange' ? 'bg-orange-500' : 'bg-blue-500'
                                    }`}>
                                    {formatTimeFromMinutes(fitDragOverData.targetTimeMinutes)} - {formatTimeFromMinutes(fitDragOverData.targetTimeMinutes + (draggedItemInfo.durationMinutes || 0))}
                                </span>
                            </div>

                            {/* Availability Marker Box */}
                            <div
                                className={`absolute border-2 rounded pointer-events-none opacity-70 ${(() => {
                                    const status = fitDragOverData.availabilityStatus?.overallStatus;
                                    const reasons = fitDragOverData.availabilityStatus?.conflictReasons || [];
                                    const isOnlyRoomOccupancy = reasons.length === 1 && reasons[0]?.reason?.startsWith('Room (') && reasons[0]?.reason?.includes(' occupied by ');
                                    if (status === 'red' && isOnlyRoomOccupancy) return 'border-blue-500 border-dashed bg-blue-300/30';
                                    if (status === 'red') return 'border-red-600 bg-red-400/50';
                                    if (status === 'orange') return 'border-orange-500 bg-orange-300/50';
                                    return 'border-blue-500 border-dashed bg-blue-300/30';
                                })()}`}
                                title={fitDragOverData.availabilityStatus?.conflictReason || 'Slot appears available'}
                                style={{
                                    top: `${((fitDragOverData.targetTimeMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * 100}%`,
                                    height: `${((draggedItemInfo.durationMinutes || 0) / TOTAL_SCHEDULE_MINUTES) * 100}%`,
                                    gridColumnStart: fitDragOverData.roomIndex + 2,
                                    gridColumnEnd: fitDragOverData.roomIndex + 3,
                                    width: '100%',
                                    zIndex: 50,
                                    gridRow: 2, // Ensure it's in the content row
                                }}
                            ></div>
                        </>
                    )}

                </div> {/* End Grid Structure Div */}
            </div> {/* End Grid Container Div */}

            {/* Availability Overlay (Rendered outside the grid, positioned absolutely relative to viewport/parent) */}
            <AvailabilityOverlay
                reasons={fitOverlayInfo.reasons}
                position={fitOverlayInfo.position}
                isVisible={fitOverlayInfo.isVisible}
            />
            {/* Read-only details for public users */}
            {!allowEditing && (
                <ScheduleEntryDetailView
                    isOpen={isDetailOpen}
                    onClose={() => setIsDetailOpen(false)}
                    scheduleEntry={detailEntry}
                    groups={groups as any}
                    subjects={subjects as any}
                    teachers={teachers as any}
                    rooms={rooms as any}
                    isPublic
                />
            )}
        </div> // End Outer Component Div
    );
}

export default DayTableFit;